import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, User, FileText, Upload, CheckCircle, AlertCircle, Briefcase, GraduationCap, MapPin, DollarSign, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import { resumeStorage, fileToBase64 } from './utils/resumeStorage';
import { resumesAPI } from '../services/api';
import { EmailVerification } from './shared/EmailVerification';

interface ResumeModalProps {
    isOpen: boolean;
    onClose: () => void;
}

// Skills List
const TECH_SKILLS = [
  "C", "C++", "C#", "Java", "JavaScript", "TypeScript", "Python", "PHP", "Go", "Rust", "Ruby",
  "Kotlin", "Swift", "Perl", "Scala", "R", "MATLAB", "Dart", "Elixir", "Erlang", "Haskell",
  "Objective-C", "VB.NET", "Groovy", "Lua", "Julia", "Bash", "PowerShell", "Assembly", "Solidity",

  "HTML", "CSS", "Sass", "Less", "Bootstrap", "Tailwind CSS", "PostCSS", "Styled Components",

  "React", "Angular", "Vue.js", "Next.js", "Nuxt.js", "Svelte", "SvelteKit", "Remix", "Alpine.js",
  "Backbone.js", "Ember.js", "Redux", "Zustand", "MobX", "RxJS", "Three.js", "D3.js", "Chart.js",

  "Node.js", "Express.js", "NestJS", "Koa", "Fastify", "Hapi", "Laravel", "CodeIgniter", "Symfony",
  "CakePHP", "Yii", "Django", "Flask", "FastAPI", "Tornado", "Spring", "Spring Boot", "Micronaut",
  "Quarkus", "ASP.NET Core", "Ruby on Rails", "Phoenix Framework", "Gin (Golang)", "Fiber (Golang)",

  "REST API", "GraphQL", "gRPC", "WebSockets", "OpenAPI/Swagger", "Postman", "Insomnia", "Apigee",

  "MySQL", "PostgreSQL", "SQLite", "Oracle Database", "MariaDB", "Microsoft SQL Server", "MongoDB",
  "Cassandra", "Redis", "Elasticsearch", "Solr", "DynamoDB", "CouchDB", "Neo4j",
  "Firebase Realtime Database", "Firestore", "Snowflake", "BigQuery", "Amazon Redshift",
  "ClickHouse", "Apache Hive", "HBase", "Delta Lake", "Apache Iceberg", "Databricks",
  "Supabase", "PlanetScale", "Neon",

  "Apache Hadoop", "Apache Spark", "Apache Kafka", "Apache Flink", "Apache Airflow", "dbt",
  "Kafka Connect", "Apache NiFi", "Apache Beam", "AWS Kinesis", "AWS Glue", "EMR",
  "Azure Data Factory", "Synapse Analytics", "Informatica", "Talend", "Matillion",

  "Git", "GitHub", "GitLab", "Bitbucket", "SVN", "Docker", "Kubernetes", "Helm", "Kustomize",
  "OpenShift", "Rancher", "Nomad", "Jenkins", "GitHub Actions", "GitLab CI/CD", "CircleCI",
  "Travis CI", "Bamboo", "TeamCity", "Ansible", "Terraform", "Packer", "Pulumi", "Chef",
  "Puppet", "SaltStack", "Argo CD", "Flux", "Istio", "Linkerd", "Consul", "Vault",
  "Prometheus", "Grafana", "ELK Stack", "EFK Stack", "OpenTelemetry", "Datadog", "New Relic",
  "PagerDuty", "Sentry", "Splunk", "Zabbix", "Nagios", "Nginx", "Apache HTTP Server",
  "HAProxy", "Traefik",

  "AWS", "Microsoft Azure", "Google Cloud", "DigitalOcean", "Heroku", "Vercel", "Netlify",
  "Cloudflare", "OpenStack", "Linode", "Oracle Cloud", "IBM Cloud", "Render", "Fly.io",

  "React Native", "Flutter", "Ionic", "SwiftUI", "Xcode", "Android Studio", "Kotlin (Android)",
  "Java (Android)", "Jetpack Compose", "Objective-C (iOS)",

  "Selenium", "Cypress", "Playwright", "Puppeteer", "Appium", "JUnit", "TestNG", "NUnit",
  "xUnit", "Jest", "Mocha", "Chai", "Karma", "Vitest", "PyTest", "Robot Framework", "Newman",
  "LoadRunner", "JMeter", "Gatling", "SonarQube", "Cucumber", "Behave",

  "OWASP", "Ethical Hacking", "Penetration Testing", "Burp Suite", "Metasploit", "Kali Linux",
  "Wireshark", "Nmap", "Nessus", "Snort", "Suricata", "SIEM", "IAM", "OAuth 2.0",
  "OpenID Connect", "SAML", "JWT", "Keycloak", "TLS/SSL", "PKI",

  "TensorFlow", "Keras", "PyTorch", "Scikit-learn", "XGBoost", "LightGBM", "CatBoost", "Pandas",
  "NumPy", "SciPy", "Matplotlib", "Seaborn", "Plotly", "Jupyter", "Google Colab", "OpenCV",
  "spaCy", "NLTK", "Hugging Face", "LangChain", "OpenAI API", "Stable Diffusion", "MLflow",
  "Kubeflow", "TensorRT", "ONNX", "Ray", "DVC", "Weights & Biases", "Seldon Core", "BentoML",
  "Vertex AI", "SageMaker", "Azure Machine Learning", "Databricks ML",

  "Tableau", "Power BI", "Looker", "Qlik Sense", "Apache Superset", "Metabase",

  "Figma", "Adobe XD", "Sketch", "InVision", "Framer", "Balsamiq", "Miro", "Zeplin", "Marvel",
  "Axure RP", "Adobe Illustrator", "Adobe Photoshop", "Adobe After Effects", "Adobe Premiere Pro",

  "Jira", "Confluence", "Trello", "Asana", "ClickUp", "Airtable", "Notion", "Slack",
  "Microsoft Teams", "Google Workspace", "Basecamp", "Monday.com", "MS Project",

  "WordPress", "Drupal", "Joomla", "Shopify", "Magento", "WooCommerce", "Wix", "Squarespace",
  "Contentful", "Strapi", "Sanity", "Ghost", "Storyblok",

  "SEO", "SEM", "Google Analytics", "GA4", "Google Tag Manager", "Google Ads", "Facebook Ads",
  "LinkedIn Ads", "HubSpot", "Mailchimp", "Ahrefs", "Moz", "Hootsuite", "Buffer", "Hotjar",
  "Mixpanel", "Amplitude",

  "TCP/IP", "DNS", "HTTP/HTTPS", "Load Balancing", "CDN", "VPN", "Proxy Servers", "VLAN", "SDN",

  "UiPath", "Automation Anywhere", "Blue Prism", "Power Automate", "Zapier", "IFTTT",

  "Salesforce", "SAP", "Oracle ERP", "Microsoft Dynamics 365", "Zoho CRM",

  "Unity", "Unreal Engine", "Godot", "Cocos2d-x",

  "Webpack", "Vite", "Rollup", "Babel", "ESLint", "Prettier", "Storybook", "Chromatic",
  "CMake", "Make", "Gradle", "Maven", "Ant", "npm", "Yarn", "pnpm", "Nx", "Turborepo",
  "Monorepo", "Prisma", "TypeORM", "Sequelize", "Mongoose", "Apollo GraphQL", "Relay",

  "Microservices", "Serverless", "Event-Driven Architecture",

  "ServiceNow", "Zendesk", "Freshdesk", "ITIL", "Six Sigma",

  "OKRs", "Roadmapping", "Stakeholder Management", "Risk Management", "Scrum", "Kanban",
  "Agile", "Gantt Charts", "Business Analysis", "Requirements Gathering", "RESTful Services"
];

const qualificationOptions = [
    'High School', 'Diploma', 'Bachelor\'s Degree', 'Master\'s Degree', 'PhD', 'Other'
];

const noticePeriodOptions = [
    'Immediate', '15 Days', '1 Month', '2 Months', '3 Months', 'Currently Serving Notice'
];


export const ResumeModal: React.FC<ResumeModalProps> = ({ isOpen, onClose }) => {
    const [experienceLevel, setExperienceLevel] = useState<'Fresher' | 'Experienced' | ''>('');
    const [formData, setFormData] = useState({
        // Personal Information
        name: '',
        gender: '',
        dob: '',
        mobile: '',
        email: '',
        currentAddress: '',
        linkedinUrl: '',
        portfolioUrl: '',

        // Education Details
        qualification: '',
        specialization: '',
        university: '',
        yearOfPassing: '',
        percentageCgpa: '',

        // Experience & Skills
        totalExperience: '',
        previousJobTitle: '',
        currentCtc: '',
        expectedSalary: '',
        noticePeriod: '',
        preferredLocation: '',
        willingToRelocate: '',
        employmentType: ''
    });

    const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
    const [skillSearchQuery, setSkillSearchQuery] = useState('');
    const [showSkillSuggestions, setShowSkillSuggestions] = useState(false);
    const [selectedResume, setSelectedResume] = useState<File | null>(null);
    const [fileError, setFileError] = useState({ resume: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);

    const modalRef = useRef<HTMLDivElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const resumeInputRef = useRef<HTMLInputElement>(null);
    const skillInputRef = useRef<HTMLInputElement>(null);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        } else {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isOpen]);

    // Close on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Handle backdrop click
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === backdropRef.current) {
            onClose();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Skills autocomplete
    const filteredSkills = TECH_SKILLS.filter(skill =>
        skill.toLowerCase().includes(skillSearchQuery.toLowerCase()) &&
        !selectedSkills.includes(skill)
    ).slice(0, 10);

    const handleSkillSelect = (skill: string) => {
        if (!selectedSkills.includes(skill)) {
            setSelectedSkills([...selectedSkills, skill]);
            setSkillSearchQuery('');
            setShowSkillSuggestions(false);
            skillInputRef.current?.focus();
        }
    };

    const handleSkillRemove = (skillToRemove: string) => {
        setSelectedSkills(selectedSkills.filter(skill => skill !== skillToRemove));
    };

    const handleSkillInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSkillSearchQuery(value);
        setShowSkillSuggestions(value.length > 0);
    };

    // Handle paste event for comma-separated values
    const handleSkillInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedText = e.clipboardData.getData('text');
        if (pastedText.includes(',')) {
            e.preventDefault();
            const skills = pastedText.split(',').map(s => s.trim()).filter(s => s.length > 0);
            skills.forEach(skill => {
                if (!selectedSkills.includes(skill)) {
                    setSelectedSkills(prev => [...prev, skill]);
                }
            });
            setSkillSearchQuery('');
            setShowSkillSuggestions(false);
        }
    };

    const handleSkillInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const value = skillSearchQuery.trim();
            
            // Check if input contains commas (comma-separated values)
            if (value.includes(',')) {
                const skills = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                skills.forEach(skill => {
                    if (!selectedSkills.includes(skill)) {
                        setSelectedSkills(prev => [...prev, skill]);
                    }
                });
                setSkillSearchQuery('');
                setShowSkillSuggestions(false);
            } else if (value) {
                // Single skill
                if (filteredSkills.length > 0 && e.key === 'Enter') {
                    handleSkillSelect(filteredSkills[0]);
                } else if (value) {
                    handleSkillSelect(value);
                }
            }
        }
    };

    // File Upload Handlers
    const handleResumeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!validTypes.includes(file.type)) {
                setFileError({ resume: 'Please upload a PDF or Word document' });
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setFileError({ resume: 'File size must be less than 5MB' });
                return;
            }
            setSelectedResume(file);
            setFileError({ resume: '' });
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Honeypot
        const hp = (e.currentTarget as any).website?.value;
        if (hp) return;

        // Check email verification
        if (!isEmailVerified) {
            toast.error('Please verify your email address before submitting');
            return;
        }

        // Basic client validation
        if (!formData.name || !formData.email || !formData.mobile || !formData.dob) {
            toast.error('Please fill in all required fields');
            return;
        }
        if (!selectedResume) {
            toast.error('Please upload your resume');
            return;
        }

        setIsSubmitting(true);
        try {
            // Convert resume file to base64 for localStorage
            const resumeBase64 = await fileToBase64(selectedResume);
            
            // Store resume in localStorage
            resumeStorage.add({
                name: formData.name,
                email: formData.email,
                phone: formData.mobile,
                location: formData.preferredLocation || formData.currentAddress || 'Not specified',
                currentRole: formData.previousJobTitle || 'Fresher',
                experience: experienceLevel === 'Fresher' ? '0 years' : `${formData.totalExperience} years`,
                education: `${formData.qualification} - ${formData.specialization}${formData.university ? ` - ${formData.university}` : ''}${formData.yearOfPassing ? ` (${formData.yearOfPassing})` : ''}`,
                summary: `${formData.qualification} with ${experienceLevel === 'Fresher' ? 'no' : formData.totalExperience + ' years of'} experience${formData.previousJobTitle ? ' as ' + formData.previousJobTitle : ''}. Skills: ${selectedSkills.join(', ')}`,
                resumeUrl: resumeBase64,
                resumeFileName: selectedResume.name,
                resumeFileType: selectedResume.type,
                linkedIn: formData.linkedinUrl,
                portfolio: formData.portfolioUrl,
                skills: selectedSkills,
                // Additional fields
                gender: formData.gender,
                dob: formData.dob,
                currentAddress: formData.currentAddress,
                qualification: formData.qualification,
                specialization: formData.specialization,
                university: formData.university,
                yearOfPassing: formData.yearOfPassing,
                percentageCgpa: formData.percentageCgpa,
                totalExperience: formData.totalExperience,
                previousJobTitle: formData.previousJobTitle,
                currentCtc: formData.currentCtc,
                expectedSalary: formData.expectedSalary,
                noticePeriod: formData.noticePeriod,
                preferredLocation: formData.preferredLocation,
                willingToRelocate: formData.willingToRelocate,
                employmentType: formData.employmentType
            });

            // Submit to MongoDB backend API (sends emails automatically)
            try {
                await resumesAPI.create({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.mobile,
                    position: formData.previousJobTitle || 'Not specified',
                    experience: experienceLevel === 'Fresher' ? '0 years' : `${formData.totalExperience} years`,
                    resumeUrl: resumeBase64,
                    resumeFileName: selectedResume.name,
                    resumeFileSize: selectedResume.size,
                    skills: selectedSkills,
                    linkedinUrl: formData.linkedinUrl,
                    portfolioUrl: formData.portfolioUrl,
                    location: formData.currentAddress || 'Not specified',
                    expectedSalary: formData.expectedSalary,
                });
                console.log('✅ Resume submitted');
            } catch (apiError: any) {
                console.error('⚠️ API submission failed (resume saved to local storage):', apiError.message);
                // Resume is already saved to localStorage, so user experience is not affected
            }

            toast.success('Resume Submitted Successfully!', {
                description: 'Your resume has been received. We\'ll contact you soon.',
                icon: <CheckCircle className="w-5 h-5" />,
            });

            // Reset form
            setFormData({
                name: '', gender: '', dob: '', mobile: '', email: '',
                currentAddress: '', linkedinUrl: '', portfolioUrl: '',
                qualification: '', specialization: '', university: '', yearOfPassing: '', percentageCgpa: '',
                totalExperience: '', previousJobTitle: '',
                currentCtc: '', expectedSalary: '',
                noticePeriod: '', preferredLocation: '', willingToRelocate: '', employmentType: ''
            });
            setSelectedSkills([]);
            setSelectedResume(null);
            setExperienceLevel('');
            setIsEmailVerified(false); // Reset email verification
            onClose();
        } catch (err: any) {
            toast.error('Submission Failed', {
                description: err?.message || 'Please try again later or contact support.',
                icon: <AlertCircle className="w-5 h-5" />,
            });
        } finally {
            setIsSubmitting(false);
        }
    };



    if (!isOpen) return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div
                    ref={backdropRef}
                    onClick={handleBackdropClick}
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                    style={{ margin: 0 }}
                >
                    <motion.div
                        ref={modalRef}
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.2 }}
                        className="relative w-full max-w-4xl max-h-[90vh] glass-strong rounded-2xl border border-primary/30 shadow-2xl flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border/30">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                                    <Briefcase className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h2 className="text-foreground">
                                        Submit Your Resume
                                    </h2>
                                    <p className="text-sm text-muted-foreground">Fill in your details to apply</p>
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                className="text-muted-foreground hover:text-foreground hover:bg-primary/10 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        {/* Modal Body */}
                        <div className="modal-scrollable flex-1 px-6 py-4 sm:py-6 overflow-y-auto">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

                                {/* SECTION 1: Personal Information */}
                                <div className="space-y-4">
                                    <h3 className="flex items-center gap-2 text-primary">
                                        <User className="w-5 h-5" />
                                        <span>Personal Information</span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Full Name */}
                                        <div className="space-y-2">
                                            <Label htmlFor="name" className="text-foreground text-sm">
                                                Full Name *
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                placeholder="Enter your full name"
                                                required
                                            />
                                        </div>

                                        {/* Email with Verification */}
                                        <EmailVerification
                                            email={formData.email}
                                            onEmailChange={(email) => setFormData(prev => ({ ...prev, email }))}
                                            onVerificationChange={setIsEmailVerified}
                                            disabled={isSubmitting}
                                            label="Email Address"
                                            required
                                        />

                                        {/* Mobile */}
                                        <div className="space-y-2">
                                            <Label htmlFor="mobile" className="text-foreground text-sm">
                                                Mobile Number *
                                            </Label>
                                            <Input
                                                id="mobile"
                                                name="mobile"
                                                type="tel"
                                                value={formData.mobile}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                placeholder="+1 234 567 8900"
                                                required
                                            />
                                        </div>

                                        {/* Date of Birth */}
                                        <div className="space-y-2">
                                            <Label htmlFor="dob" className="text-foreground text-sm">
                                                Date of Birth *
                                            </Label>
                                            <Input
                                                id="dob"
                                                name="dob"
                                                type="date"
                                                value={formData.dob}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                required
                                            />
                                        </div>

                                        {/* LinkedIn Profile */}
                                        <div className="md:col-span-2 space-y-2">
                                            <Label htmlFor="linkedinUrl" className="text-foreground text-sm">
                                                LinkedIn Profile URL *
                                            </Label>
                                            <Input
                                                id="linkedinUrl"
                                                name="linkedinUrl"
                                                type="url"
                                                value={formData.linkedinUrl}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                placeholder="https://linkedin.com/in/yourprofile"
                                                required
                                            />
                                        </div>

                                        {/* Portfolio URL */}
                                        <div className="md:col-span-2 space-y-2">
                                            <Label htmlFor="portfolioUrl" className="text-foreground text-sm">
                                                Portfolio URL
                                            </Label>
                                            <Input
                                                id="portfolioUrl"
                                                name="portfolioUrl"
                                                type="url"
                                                value={formData.portfolioUrl}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                placeholder="https://yourportfolio.com"
                                            />
                                        </div>

                                        {/* Current Address */}
                                        <div className="md:col-span-2 space-y-2">
                                            <Label htmlFor="currentAddress" className="text-foreground text-sm">
                                                Current Address
                                            </Label>
                                            <Textarea
                                                id="currentAddress"
                                                name="currentAddress"
                                                value={formData.currentAddress}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50 min-h-[80px]"
                                                placeholder="Street, City, State, ZIP"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* SECTION 2: Education Details */}
                                <div className="space-y-4">
                                    <h3 className="flex items-center gap-2 text-primary">
                                        <GraduationCap className="w-5 h-5" />
                                        <span>Education Details</span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Qualification */}
                                        <div className="space-y-2">
                                            <Label htmlFor="qualification" className="text-foreground text-sm">
                                                Highest Qualification *
                                            </Label>
                                            <Select value={formData.qualification} onValueChange={(value) => setFormData(prev => ({ ...prev, qualification: value }))}>
                                                <SelectTrigger className="glass border-border/30 focus:border-primary/50">
                                                    <SelectValue placeholder="Select" />
                                                </SelectTrigger>
                                                <SelectContent className="dropdown-bg z-[100000]">
                                                    {qualificationOptions.map((qual) => (
                                                        <SelectItem key={qual} value={qual}>{qual}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Specialization */}
                                        <div className="space-y-2">
                                            <Label htmlFor="specialization" className="text-foreground text-sm">
                                                Specialization *
                                            </Label>
                                            <Input
                                                id="specialization"
                                                name="specialization"
                                                type="text"
                                                value={formData.specialization}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                placeholder="e.g. Computer Science"
                                                required
                                            />
                                        </div>

                                        {/* University */}
                                        <div className="space-y-2">
                                            <Label htmlFor="university" className="text-foreground text-sm">
                                                University/College Name
                                            </Label>
                                            <Input
                                                id="university"
                                                name="university"
                                                type="text"
                                                value={formData.university}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                placeholder="Institution name"
                                            />
                                        </div>

                                        {/* Year of Passing */}
                                        <div className="space-y-2">
                                            <Label htmlFor="yearOfPassing" className="text-foreground text-sm">
                                                Year of Passing
                                            </Label>
                                            <Input
                                                id="yearOfPassing"
                                                name="yearOfPassing"
                                                type="number"
                                                value={formData.yearOfPassing}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                placeholder="YYYY"
                                                min="1950"
                                                max="2030"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* SECTION 3: Experience & Skills */}
                                <div className="space-y-4">
                                    <h3 className="flex items-center gap-2 text-primary">
                                        <Clock className="w-5 h-5" />
                                        <span>Experience & Skills</span>
                                    </h3>

                                    {/* Experience Level Radio */}
                                    <div className="space-y-3 p-4 glass-strong rounded-lg border border-primary/20">
                                        <Label className="text-foreground">
                                            Experience Level *
                                        </Label>
                                        <RadioGroup value={experienceLevel} onValueChange={(value) => setExperienceLevel(value as 'Fresher' | 'Experienced' | '')}>
                                            <div className="flex items-center space-x-6">
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="Fresher" id="fresher" />
                                                    <Label htmlFor="fresher" className="cursor-pointer">Fresher</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <RadioGroupItem value="Experienced" id="experienced" />
                                                    <Label htmlFor="experienced" className="cursor-pointer">Experienced</Label>
                                                </div>
                                            </div>
                                        </RadioGroup>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Experience Fields - Only show for Experienced */}
                                        {experienceLevel === 'Experienced' && (
                                            <>
                                                {/* Total Experience */}
                                                <div className="space-y-2">
                                                    <Label htmlFor="totalExperience" className="text-foreground text-sm">
                                                        Total Experience (Years) *
                                                    </Label>
                                                    <Input
                                                        id="totalExperience"
                                                        name="totalExperience"
                                                        type="number"
                                                        value={formData.totalExperience}
                                                        onChange={handleInputChange}
                                                        className="glass border-border/30 focus:border-primary/50"
                                                        placeholder="0"
                                                        min="0"
                                                        step="0.5"
                                                        required
                                                    />
                                                </div>

                                                {/* Previous Job Title */}
                                                <div className="space-y-2">
                                                    <Label htmlFor="previousJobTitle" className="text-foreground text-sm">
                                                        Current/Previous Role
                                                    </Label>
                                                    <Input
                                                        id="previousJobTitle"
                                                        name="previousJobTitle"
                                                        type="text"
                                                        value={formData.previousJobTitle}
                                                        onChange={handleInputChange}
                                                        className="glass border-border/30 focus:border-primary/50"
                                                        placeholder="Job title"
                                                    />
                                                </div>

                                                {/* Current CTC */}
                                                <div className="space-y-2">
                                                    <Label htmlFor="currentCtc" className="text-foreground text-sm">
                                                        <DollarSign className="w-3 h-3 inline mr-1" />
                                                        Current CTC
                                                    </Label>
                                                    <Input
                                                        id="currentCtc"
                                                        name="currentCtc"
                                                        type="text"
                                                        value={formData.currentCtc}
                                                        onChange={handleInputChange}
                                                        className="glass border-border/30 focus:border-primary/50"
                                                        placeholder="e.g. $80,000"
                                                    />
                                                </div>
                                            </>
                                        )}

                                        {/* Expected Salary - Always show */}
                                        <div className="space-y-2">
                                            <Label htmlFor="expectedSalary" className="text-foreground text-sm">
                                                <DollarSign className="w-3 h-3 inline mr-1" />
                                                Expected Salary *
                                            </Label>
                                            <Input
                                                id="expectedSalary"
                                                name="expectedSalary"
                                                type="text"
                                                value={formData.expectedSalary}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                placeholder="e.g. $90,000"
                                                required
                                            />
                                        </div>

                                        {/* Notice Period - Only for Experienced */}
                                        {experienceLevel === 'Experienced' && (
                                            <div className="space-y-2">
                                                <Label htmlFor="noticePeriod" className="text-foreground text-sm">
                                                    Notice Period
                                                </Label>
                                                <Select value={formData.noticePeriod} onValueChange={(value) => setFormData(prev => ({ ...prev, noticePeriod: value }))}>
                                                    <SelectTrigger className="glass border-border/30 focus:border-primary/50">
                                                        <SelectValue placeholder="Select notice period" />
                                                    </SelectTrigger>
                                                    <SelectContent className="dropdown-bg z-[100000]">
                                                        {noticePeriodOptions.map((period) => (
                                                            <SelectItem key={period} value={period}>{period}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}

                                        {/* Preferred Location */}
                                        <div className="space-y-2">
                                            <Label htmlFor="preferredLocation" className="text-foreground text-sm">
                                                <MapPin className="w-3 h-3 inline mr-1" />
                                                Preferred Location
                                            </Label>
                                            <Input
                                                id="preferredLocation"
                                                name="preferredLocation"
                                                type="text"
                                                value={formData.preferredLocation}
                                                onChange={handleInputChange}
                                                className="glass border-border/30 focus:border-primary/50"
                                                placeholder="City, State"
                                            />
                                        </div>

                                        {/* Willing to Relocate */}
                                        <div className="space-y-2">
                                            <Label className="text-foreground text-sm">
                                                Willing to Relocate? *
                                            </Label>
                                            <RadioGroup
                                                value={formData.willingToRelocate}
                                                onValueChange={(value) => setFormData(prev => ({ ...prev, willingToRelocate: value }))}
                                            >
                                                <div className="flex items-center space-x-6">
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value="Yes" id="relocate-yes" />
                                                        <Label htmlFor="relocate-yes" className="cursor-pointer">Yes</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value="No" id="relocate-no" />
                                                        <Label htmlFor="relocate-no" className="cursor-pointer">No</Label>
                                                    </div>
                                                </div>
                                            </RadioGroup>
                                        </div>

                                        {/* Skills Section - Full width */}
                                        <div className="md:col-span-2 space-y-2">
                                            <Label className="text-foreground text-sm">
                                                Skills *
                                            </Label>

                                            {/* Selected Skills Display */}
                                            {selectedSkills.length > 0 && (
                                                <div className="flex flex-wrap gap-2 p-3 glass rounded-lg border border-border/30">
                                                    {selectedSkills.map((skill) => (
                                                        <span
                                                            key={skill}
                                                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary/20 text-primary border border-primary/30 rounded-full text-sm"
                                                        >
                                                            {skill}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSkillRemove(skill)}
                                                                className="hover:text-primary/70"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Skill Input with Autocomplete */}
                                            <div className="relative">
                                                <Input
                                                    ref={skillInputRef}
                                                    type="text"
                                                    value={skillSearchQuery}
                                                    onChange={handleSkillInputChange}
                                                    onKeyDown={handleSkillInputKeyDown}
                                                    onPaste={handleSkillInputPaste}
                                                    onFocus={() => setShowSkillSuggestions(skillSearchQuery.length > 0)}
                                                    className="glass border-border/30 focus:border-primary/50"
                                                    placeholder="Type to search and add skills (e.g., React, Python)..."
                                                />

                                                {/* Autocomplete Dropdown */}
                                                {showSkillSuggestions && filteredSkills.length > 0 && (
                                                    <div className="absolute z-50 w-full mt-1 glass border border-border/30 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                        {filteredSkills.map((skill) => (
                                                            <button
                                                                key={skill}
                                                                type="button"
                                                                onClick={() => handleSkillSelect(skill)}
                                                                className="w-full text-left px-4 py-2 hover:bg-primary/10 text-foreground transition-colors"
                                                            >
                                                                {skill}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Type and select from dropdown, press Enter to add, or paste comma-separated skills (e.g., "React, Python, Docker")
                                            </p>
                                        </div>


                                    </div>
                                </div>

                                {/* SECTION 4: Resume Upload */}
                                <div className="space-y-4">
                                    <h3 className="flex items-center gap-2 text-primary">
                                        <FileText className="w-5 h-5" />
                                        <span>Resume Upload</span>
                                    </h3>

                                    <div className="space-y-2">
                                        <Label htmlFor="resume" className="text-foreground text-sm">
                                            Upload Resume (PDF or Word) *
                                        </Label>
                                        <div className="flex flex-col gap-3">
                                            <input
                                                ref={resumeInputRef}
                                                id="resume"
                                                name="resume"
                                                type="file"
                                                accept=".pdf,.doc,.docx"
                                                onChange={handleResumeUpload}
                                                className="hidden"
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => resumeInputRef.current?.click()}
                                                className="glass border-border/30 hover:border-primary/50"
                                            >
                                                <Upload className="w-4 h-4 mr-2" />
                                                Choose File
                                            </Button>
                                            {selectedResume && (
                                                <span className="text-sm text-muted-foreground flex items-center gap-2">
                                                    <CheckCircle className="w-4 h-4 text-accent" />
                                                    <span className="truncate">{selectedResume.name}</span>
                                                </span>
                                            )}
                                            {fileError.resume && (
                                                <p className="text-sm text-destructive flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" />
                                                    {fileError.resume}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={onClose}
                                        className="glass border-border/30 hover:border-primary/30"
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="bg-primary text-primary-foreground hover:neon-glow"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Clock className="w-4 h-4 mr-2 animate-spin" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4 mr-2" />
                                                Submit Application
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};